import { Injectable } from '@jkhy/frontend-utils/di';
import { exists } from '@jkhy/frontend-utils/functions';
import {
    AchExceptionDetailDataDto,
    AchExceptionDetailSearchModelDto,
    AchExceptionsDecisionsActivityRequestModelDto,
    ArpClient,
    CheckExceptionClient,
    type CheckExceptionModelDto,
    CompanyAccountsClient,
    CompanyOptionModelDto,
    DashboardClient,
    ErrorResponseModelDto,
    PositivePayCompanyConfigurationClient,
    type SearchCheckExceptionModelDto,
} from '@treasury/api/channel';
import { HttpResponse } from '@treasury/api/channel/generated/http-client';
import { AchException, CheckException } from '../../../arp';
import { mapAchExceptionFiltersToRequest } from '../../../arp/mappings/ach-exception-filters-mapping';
import { mapAchExceptionsActivityFiltersToRequest } from '../../../arp/mappings/ach-exceptions-activity-filters-mapping';
import { PayOrReturnChoice } from '../../mappings/ach';
import { CheckExceptionsService } from './check-exceptions-service';
import { CompanyAccount } from './entities/company-account';
import { PositivePayOption } from './entities/positive-pay-option';

@Injectable()
export class PositivePayService {
    constructor(
        private readonly dashboardClient: DashboardClient,
        private readonly arpClient: ArpClient,
        private readonly checkExceptionClient: CheckExceptionClient,
        private readonly companyAccountsClient: CompanyAccountsClient,
        private readonly positivePayCompanyConfigurationClient: PositivePayCompanyConfigurationClient,
        private readonly checkExceptionService: CheckExceptionsService
    ) {}

    /**
     * Retrieves all positive pay options from the company configuration.
     *
     * This method fetches all options from the positive pay company configuration and filters out any options that do not have both a string name and a string value.
     * It then maps the filtered options into instances of PositivePayOption and returns them.
     *
     * @returns {Promise<PositivePayOption[]>} A promise that resolves to an array of PositivePayOption objects.
     */
    public async getPositivePayOptions() {
        const response =
            await this.positivePayCompanyConfigurationClient.positivePayCompanyConfigurationGetAllOptions();
        const options = response.data.filter((option: CompanyOptionModelDto) => {
            return typeof option.value === 'string' && typeof option.name === 'string';
        });

        return options.map(
            (option: CompanyOptionModelDto) =>
                new PositivePayOption({
                    name: option.name,
                    value: option.value,
                })
        );
    }

    public async getAchExceptions() {
        const response = (await this.dashboardClient.dashboardGetWorkExceptions({
            type: 'achExceptions',
        })) as HttpResponse<AchExceptionDetailDataDto[], ErrorResponseModelDto>;

        if (!exists(response.data)) {
            return [];
        }

        return response.data.map((d: AchExceptionDetailDataDto) => new AchException(d));
    }

    public async searchAchExceptions(params: AchExceptionDetailSearchModelDto) {
        const request = mapAchExceptionFiltersToRequest(params);
        const response = await this.arpClient.arpFindAchExceptions(request);
        if (!response.data.achExceptionDetails) {
            return [];
        }
        // Fixes bad typing from the API
        return response.data.achExceptionDetails.map(
            d => new AchException(d as unknown as AchExceptionDetailDataDto)
        );
    }

    public async searchAchExceptionsActivity(
        params: AchExceptionsDecisionsActivityRequestModelDto | null
    ) {
        const request = mapAchExceptionsActivityFiltersToRequest(
            params
        ) as unknown as AchExceptionsDecisionsActivityRequestModelDto;
        const response = await this.arpClient.arpFindAchExceptionsDecisionsActivity(request);
        return response.data.items;
    }

    public async searchPaginatedAchExceptionsActivity(
        params?: AchExceptionsDecisionsActivityRequestModelDto
    ) {
        const request = mapAchExceptionsActivityFiltersToRequest(
            params
        ) as unknown as AchExceptionsDecisionsActivityRequestModelDto;
        const response = await this.arpClient.arpFindAchExceptionsDecisionsActivity(request);
        return {
            achExceptionDecisions: response.data.items,
            count: response.data.totalCount,
        };
    }

    public async getArpSecCodes() {
        const response = await this.arpClient.arpGetAllSecCodes();
        return response.data;
    }

    /**
     * Retrieves ARP accounts related to ACH exceptions.
     *
     * @returns {Promise<any>} A promise that resolves to the ARP accounts data.
     */
    public async getArpAchExceptionAccounts() {
        const response = await this.arpClient.arpGetArpAccounts('WorkAchException');
        return response.data.map(d => new CompanyAccount(d));
    }

    /**
     * Retrieves ARP accounts related to exceptions.
     *
     * @returns {Promise<any>} A promise that resolves to the ARP accounts data.
     */
    public async getArpExceptionAccounts() {
        const response = await this.arpClient.arpGetArpAccounts('WorkArpExceptions');
        return response.data.map(d => new CompanyAccount(d));
    }

    public async searchCheckExceptionsDecisionActivity(params: SearchCheckExceptionModelDto) {
        const response = await this.checkExceptionClient.checkExceptionSearchCheckExceptions({
            ...params,
            type: 'History',
            isPortrait: params.isPortrait ?? false,
        });

        if (!response.data?.items) {
            return [];
        }

        return response.data.items.map(item => new CheckException(item as CheckExceptionModelDto));
    }

    public async getArpDownloadAccounts() {
        const response = await this.arpClient.arpGetArpAccounts('DownloadArpFile');
        return response.data.map(d => new CompanyAccount(d));
    }

    public async getArpUploadAccounts() {
        const response = await this.arpClient.arpGetArpAccounts('UploadArpFile');
        return response.data.map(d => new CompanyAccount(d));
    }

    public async getReturnReasons() {
        return CheckExceptionsService.getCheckExceptionReturnReasons();
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    public async requestExceptionCorrection(file: any, correctionRequest: any) {
        return CheckExceptionsService.saveCorrectionRequest(file, correctionRequest);
    }

    public async getCutoffTimes() {
        const response = await this.companyAccountsClient.companyAccountsGetCutoffTimes();
        return response.data;
    }

    public async getCheckExceptions() {
        const arpExceptionAccounts = (
            await this.arpClient.arpGetArpAccounts('WorkArpExceptions', {})
        ).data.map(d => new CompanyAccount(d));
        const params = {
            parameters: {
                account: arpExceptionAccounts
                    .map((account: CompanyAccount) => account.accountUniqueId)
                    .filter(exists),
            },
        };

        const checkExceptionAggregate =
            await this.checkExceptionService.searchCheckExceptions(params);
        return checkExceptionAggregate;
    }

    public async updateExceptionStatuses(exceptions: AchException[]) {
        const updated = exceptions
            .filter(e => e.decisionStatusId !== PayOrReturnChoice.Unselected)
            .map(e => {
                const dto = e.toAchExceptionDetailModelDto();
                dto.achExceptionDecisionStatusId = e.decisionStatusId;
                return dto;
            });

        const response = await this.arpClient.arpSaveAchExceptions(updated);
        if (!response.data) {
            return false;
        }
        if ((response.data.failedDecisionsCount ?? 0) > 0) {
            return false;
        }
        return response.data;
    }
}
